import { makeAutoObservable, runInAction } from "mobx";

import { updateDocument, fetchDocument, updateDocumentPublicFlags } from "@/api";

import type {
  Document,
  TeamItemUserPermissions,
  UpdateDocumentBody,
  UpdateDocumentPublicFlagsBody,
  UpdateDocumentLockBody,
  UserShort,
} from "@/api";
import type { DocumentModel } from "@/models/DocumentModel";

const DEFAULT_PERMISSIONS: TeamItemUserPermissions = {
  read: true,
  write: true,
  comment: true,
};

/**
 * Document raw data from API and tools for fetch and update it.
 */
export class DocumentApiModel {
  doc: DocumentModel;

  private documentData?: Document;

  private fetched = false;

  private justMentionedUsers: UserShort[] = [];

  constructor(doc: DocumentModel, documentData?: Document) {
    this.doc = doc;
    this.documentData = documentData;
    makeAutoObservable(this);
  }

  get uid() {
    return this.doc.id;
  }

  get permissions() {
    return this.documentData?.userPermissions || DEFAULT_PERMISSIONS;
  }

  get publicRead() {
    return !!this.documentData?.publicRead;
  }

  get publicWrite() {
    return !!this.documentData?.publicWrite;
  }

  get locked() {
    return !!this.documentData?.locked;
  }

  get canToggleLock() {
    return !!this.documentData?.canToggleLock;
  }

  setLocked(locked: boolean) {
    if (this.documentData) {
      runInAction(() => {
        this.documentData = { ...this.documentData, locked } as Document;
      });
    }
  }

  get team() {
    return this.documentData?.team;
  }

  get title() {
    return this.documentData?.title;
  }

  get unreadMessagesCount() {
    return this.documentData?.unreadMessagesCount;
  }

  get permissionRequestScope() {
    return this.documentData?.permissionRequestScope;
  }

  get mentions(): UserShort[] {
    return [...(this.documentData?.mentions ?? []), ...this.justMentionedUsers];
  }

  rememberMentionedUser(user: UserShort): void {
    if (this.mentions.some(({ uid }) => uid === user.uid)) return;

    this.justMentionedUsers.push(user);
  }

  get isFetched() {
    return this.fetched;
  }

  async refetch() {
    if (this.uid) {
      // TODO: dispose
      try {
        const result = await fetchDocument(this.uid);
        runInAction(() => {
          this.documentData = result.document;
          this.fetched = true;
        });
      } catch {
        // TODO: handle error
        // Document may not be accessible (404, no permissions, etc.)
        // This is a best-effort data fetch.
      }
    }
  }

  async update(body: UpdateDocumentBody) {
    await this.optimisticUpdate(body, async () => {
      if (!this.uid || !this.permissions.write) {
        return;
      }
      // TODO: dispose
      const response = await updateDocument(this.uid, body);
      return response.document;
    });
  }

  async updatePublicFlags(body: UpdateDocumentPublicFlagsBody) {
    await this.optimisticUpdate(body, async () => {
      if (!this.uid || !this.permissions.write) {
        return;
      }

      // TODO: dispose
      const response = await updateDocumentPublicFlags(this.uid, body);
      return response.document;
    });
  }

  // TODO: move optimisticUpdate to apiCache
  private async optimisticUpdate(
    body: UpdateDocumentBody | UpdateDocumentPublicFlagsBody | UpdateDocumentLockBody,
    cb: () => Promise<Document | undefined>,
  ) {
    if (this.documentData) {
      const originalDocumentData = { ...this.documentData } as Document;
      runInAction(() => {
        this.documentData = { ...originalDocumentData, ...body };
      });
      try {
        const result = await cb();
        runInAction(() => {
          if (result) {
            this.documentData = result;
          } else {
            this.documentData = originalDocumentData;
          }
        });
      } catch (e) {
        runInAction(() => {
          this.documentData = originalDocumentData;
        });
        throw e;
      }
    }
  }
}
